<template>
  <div v-if="isInternalUser" class="internal-footer">
    <!-- Version Badge -->
    <div class="version-badge" :title="`Version ${version} (${environment})`">
      <i class="fas fa-code-branch"></i>
      <span>v{{ version }}</span>
    </div>
    
    <!-- What's New Button (with indicator for unread) -->
    <button 
      class="footer-btn whats-new-btn"
      :class="{ 'has-unread': hasUnreadReleaseNotes }"
      @click="showWhatsNew = true"
      title="What's New"
    >
      <i class="fas fa-gift"></i>
      <span>What's New</span>
      <span v-if="hasUnreadReleaseNotes" class="badge">!</span>
    </button>
    
    <!-- Feedback Button -->
    <button 
      class="footer-btn feedback-btn"
      @click="showFeedback = true"
      title="Report Bug or Feedback"
    >
      <i class="fas fa-bug"></i>
      <span>Feedback</span>
    </button>
    
    <!-- What's New Modal -->
    <WhatsNewModal 
      v-if="showWhatsNew" 
      :release-notes="releaseNotes"
      @close="handleWhatsNewClose"
    />
    
    <!-- Feedback Modal -->
    <FeedbackModal 
      v-if="showFeedback" 
      @close="showFeedback = false"
      @submitted="handleFeedbackSubmitted"
    />
  </div>
</template>

<script>
import observabilityAPI from '@/services/observabilityAPI';
import WhatsNewModal from './WhatsNewModal.vue';
import FeedbackModal from './FeedbackModal.vue';

export default {
  name: 'InternalFooter',
  
  components: {
    WhatsNewModal,
    FeedbackModal,
  },
  
  data() {
    return {
      version: '',
      environment: '',
      isInternalUser: false,
      hasUnreadReleaseNotes: false,
      releaseNotes: [],
      showWhatsNew: false,
      showFeedback: false,
      loaded: false,
    };
  },
  
  async mounted() {
    await this.loadVersionInfo();
    
    // Auto-show What's New if there are unread notes
    if (this.hasUnreadReleaseNotes && this.isInternalUser) {
      // Small delay to let the page load first
      setTimeout(() => {
        this.showWhatsNew = true;
      }, 1500);
    }
  },
  
  methods: {
    async loadVersionInfo() {
      try {
        const info = await observabilityAPI.getVersionInfo();
        this.version = info.version;
        this.environment = info.environment;
        this.isInternalUser = info.is_internal_user;
        this.hasUnreadReleaseNotes = info.has_unread_release_notes;
        
        // Pre-load release notes if internal user
        if (this.isInternalUser) {
          const notes = await observabilityAPI.getReleaseNotes();
          this.releaseNotes = notes.notes || [];
        }
        
        this.loaded = true;
      } catch (error) {
        console.error('Failed to load version info:', error);
        // Silently fail - footer just won't show
      }
    },
    
    async handleWhatsNewClose() {
      this.showWhatsNew = false;
      
      // Mark version as seen
      if (this.hasUnreadReleaseNotes) {
        try {
          await observabilityAPI.markVersionSeen();
          this.hasUnreadReleaseNotes = false;
        } catch (error) {
          console.error('Failed to mark version seen:', error);
        }
      }
    },
    
    handleFeedbackSubmitted(feedback) {
      this.showFeedback = false;
      // Could show a toast notification here
      console.log('Feedback submitted:', feedback.id);
    },
  },
};
</script>

<style scoped>
.internal-footer {
  position: fixed;
  bottom: 0;
  left: 250px; /* Match sidebar width */
  right: 0;
  height: 36px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 16px;
  z-index: 1000;
  font-size: 12px;
}

/* Adjust when sidebar is collapsed */
.sidebar.collapsed ~ .main-content .internal-footer {
  left: 60px;
}

.version-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: #a0aec0;
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 11px;
}

.version-badge i {
  font-size: 10px;
  color: #68d391;
}

.footer-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: #e2e8f0;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.footer-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.footer-btn i {
  font-size: 12px;
}

.whats-new-btn i {
  color: #f6ad55;
}

.whats-new-btn.has-unread {
  background: rgba(246, 173, 85, 0.2);
  border-color: #f6ad55;
  animation: pulse 2s infinite;
}

.whats-new-btn .badge {
  background: #f6ad55;
  color: #1a1a2e;
  padding: 1px 6px;
  border-radius: 10px;
  font-weight: bold;
  font-size: 10px;
  margin-left: 4px;
}

.feedback-btn i {
  color: #fc8181;
}

.feedback-btn:hover {
  border-color: #fc8181;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* Responsive - hide on mobile */
@media (max-width: 768px) {
  .internal-footer {
    left: 0;
    padding: 0 8px;
  }
  
  .footer-btn span {
    display: none;
  }
  
  .version-badge span {
    display: none;
  }
}
</style>
